import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { colorTheme } from "../../theme";
import { DragAndDropProps } from "./DragAndDrop.types";
import Add from "../../assets/DragAndDrop/Add.svg";
import TrashCan from "../../assets/DragAndDrop/TrashCan.svg";
import Search from "../../assets/DragAndDrop/Search.svg";
import {
  DragDropContainer,
  Header,
  Icon,
  ListContainer,
  Searchbar,
  SearchContainer,
  SearchIcon,
  Title,
  DraggableContainer,
  PlaceHolder,
  PlaceHolderContainer,
  DraggableItemContainer,
  DraggableItem,
  ColorBar,
  Content,
} from "./DragAndDrop.styles";

export const DragAndDrop: React.FC<DragAndDropProps> = ({ data }) => {
  const [lists, setLists] = useState(data);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMaxCapacity, setMaxCapacity] = useState(
    data.topThemes.length >= 5 ? true : false
  );
  useEffect(() => {
    setLists(data);
  }, [data]);

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    background: isDragging ? `${colorTheme.white}` : "",
    border: isDragging ? `1px solid ${colorTheme.grayLightest}` : "",
    boxShadow: isDragging ? "0px 4px 18px rgba(0, 0, 0, 0.06)" : "",

    // styles required for draggables
    ...draggableStyle,
  });

  const reorderCurrList = (list: any, startIndex: number, endIndex: number) => {
    const [removed] = list.splice(startIndex, 1);
    list.splice(endIndex, 0, removed);

    return list;
  };

  const deleteTheme = (idx: number) => {
    const newState = { ...lists };
    const [removed] = newState.topThemes.splice(idx, 1);

    newState.availableThemes.push(removed);
    newState.availableThemes = sortByGroupAndName(newState.availableThemes);

    setLists(newState);
  };

  const addTheme = (idx: number) => {
    if (lists.topThemes.length >= 5) return;
    const newState = { ...lists };
    const [removed] = newState.availableThemes.splice(idx, 1);

    newState.topThemes.push(removed);
    setLists(newState);
  };

  const onDragStart = (result: any) => {
    if (
      lists.topThemes.length >= 5 &&
      result.source.droppableId === "availableThemes"
    ) {
      setMaxCapacity(true);
    } else {
      setMaxCapacity(false);
    }
  };

  const onDragEnd = (result: any) => {
    const { source, destination, draggableId } = result;
    let newState;

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === "topThemes") {
        const reOrderedList = reorderCurrList(
          lists[source.droppableId],
          source.index,
          destination.index
        );

        newState = { ...lists };
        newState[source.droppableId] = reOrderedList;
      } else {
        return;
      }
    } else {
      newState = moveToNextList(source, destination, draggableId);
    }

    setLists(newState);
  };

  const moveToNextList = (source: any, destination: any, draggableId: any) => {
    const sourceList = lists[source.droppableId];
    const destinationList = lists[destination.droppableId];

    const removed = sourceList.find((theme: any) => theme.id === draggableId);
    destinationList.splice(destination.index, 0, removed);

    const result: any = {};
    if (destination.droppableId === "topThemes") {
      result.topThemes = destinationList;
      result.availableThemes = sourceList.filter((theme: any) => {
        return theme.id !== draggableId;
      });
    } else if (destination.droppableId === "availableThemes") {
      result.topThemes = sourceList.filter((theme: any) => {
        return theme.id !== draggableId;
      });
      result.availableThemes = sortByGroupAndName(destinationList);
    }

    return result;
  };

  const sortByGroupAndName = (list: any) => {
    return list.sort((a: any, b: any) => {
      if (a.themeGroup === b.themeGroup) {
        return a.themeName > b.themeName
          ? 1
          : b.themeName > a.themeName
          ? -1
          : 0;
      } else {
        return a.themeGroup > b.themeGroup
          ? 1
          : b.themeGroup > a.themeGroup
          ? -1
          : 0;
      }
    });
  };

  const editSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <DragDropContainer>
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <ListContainer>
          <Header>
            <Title>StrengthsFinder Top 5 Themes</Title>
          </Header>
          <Droppable
            isDropDisabled={isMaxCapacity}
            droppableId={"topThemes"}
            type="THEMES"
          >
            {(provided: any, snapshot: any) => (
              <DraggableContainer ref={provided.innerRef}>
                <PlaceHolderContainer>
                  <PlaceHolder />
                  <PlaceHolder />
                  <PlaceHolder />
                  <PlaceHolder />
                  <PlaceHolder />
                </PlaceHolderContainer>
                <DraggableItemContainer>
                  {lists.topThemes.map((theme: any, index: any) => (
                    // TODO: DragAndDrop item in separate component
                    <Draggable
                      key={theme.id}
                      draggableId={theme.id}
                      index={index}
                    >
                      {(provided: any) => (
                        <DraggableItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          // required styles from library
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style
                          )}
                        >
                          <ColorBar color={`#${theme.themeColor}`} />
                          <Content>
                            {theme.themeName}
                            <Icon
                              src={TrashCan}
                              onClick={() => deleteTheme(index)}
                            />
                          </Content>
                        </DraggableItem>
                      )}
                    </Draggable>
                  ))}
                </DraggableItemContainer>
                {provided.placeholder}
              </DraggableContainer>
            )}
          </Droppable>
        </ListContainer>

        <ListContainer available>
          <Header available>
            <Title>Available Themes</Title>
            <SearchContainer>
              <SearchIcon src={Search} />
              <Searchbar
                type="text"
                value={searchTerm}
                onChange={editSearchTerm}
              />
            </SearchContainer>
          </Header>
          <Droppable droppableId={"availableThemes"} type="THEMES">
            {(provided: any) => (
              <DraggableContainer ref={provided.innerRef} available>
                {lists.availableThemes
                  .filter((theme: any) =>
                    theme.themeName
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                  .map((theme: any, index: any) => (
                    // TODO: DragAndDrop item in separate component
                    <Draggable
                      key={theme.id}
                      draggableId={theme.id}
                      index={index}
                    >
                      {(provided: any, snapshot: any) => (
                        <DraggableItem
                          available
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          snapshot={snapshot}
                          // required styles from library
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style
                          )}
                        >
                          <ColorBar color={`#${theme.themeColor}`} />
                          <Content available>
                            {theme.themeName}
                            <Icon src={Add} onClick={() => addTheme(index)} />
                          </Content>
                        </DraggableItem>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </DraggableContainer>
            )}
          </Droppable>
        </ListContainer>
      </DragDropContext>
    </DragDropContainer>
  );
};
